// @flow

import React, { type Node } from 'react';

import * as processors from './processors';

import type {
  ApiKeys,
  HostedFieldAdditionalData,
  HostedFieldProcessorInterface,
  HostedFieldToken,
  HostedFieldType,
  HostedFieldValue,
  PaymentProcessor,
} from '@zapier/payment/src/types';

type Props = {
  /** Payment processor API keys */
  apiKeys: ApiKeys,
  /** Payment processor to use */
  paymentProcessor: PaymentProcessor,
  children: (
    createToken: (
      additionalData: HostedFieldAdditionalData
    ) => Promise<HostedFieldToken>
  ) => Node,
  /** Call when field changes */
  onChange: (name: HostedFieldType, value: HostedFieldValue) => void,
};

type State = {
  paymentProcessor: ?HostedFieldProcessorInterface,
};

const createPaymentProcessor = (
  apiKeys: ApiKeys,
  paymentProcessor: PaymentProcessor
): HostedFieldProcessorInterface =>
  processors[paymentProcessor](apiKeys[paymentProcessor]);

const HostedFieldStyle = {
  // Custom font only works with Stripe for now as Braintree does not support it.
  // I've revived an issue on their side as technically they could support it but their
  // justification is vague:
  //   https://github.com/braintree/braintree-web/issues/75#issuecomment-441748583
  fontFamily: '"Open Sans", Helvetica, arial, sans-serif',
  fontSize: '14px',
  color: '#5f6c72',
  '::placeholder': {
    color: '#969ea2',
  },
};

export default class HostedFieldsProvider extends React.Component<
  Props,
  State
> {
  state = {
    paymentProcessor: null,
  };

  async componentDidMount() {
    const paymentProcessor = await createPaymentProcessor(
      this.props.apiKeys,
      this.props.paymentProcessor
    );

    try {
      await paymentProcessor.createFields(
        HostedFieldStyle,
        this.props.onChange
      );
    } catch (error) {
      // If the user switches to another page or to Paypal quickly
      // an error may be thrown here because the DOM elements won't exist anymore.
      // In that case we silently fail here.
      return;
    }

    this.setState({ paymentProcessor });
  }

  componentWillUnmount() {
    const { state } = this;
    if (state.paymentProcessor) {
      state.paymentProcessor.destroyFields();
    }
  }

  render() {
    const { props, state } = this;
    const createToken = (
      additionalData: HostedFieldAdditionalData
    ): Promise<HostedFieldToken> => {
      if (!state.paymentProcessor)
        throw new Error('Payment provider is not ready.');
      return state.paymentProcessor.createToken(additionalData);
    };

    return props.children(createToken);
  }
}
